"use client";

import React, { useCallback } from "react";
import { TooltipWithContent } from "@/components/ui/tooltip";
import { TagsFieldDisplay } from "../TagsFieldDisplay/TagsFieldDisplay";
import { ListFieldEditor } from "../ListFieldEditor/ListFieldEditor";
import type { TagsFieldValue } from "@/components/shared/types";

interface TagsFieldCellProps {
  value: TagsFieldValue;
  fieldId: string;
  entityId: string;
  entityName: string;
  onSave: (newValue: string[]) => Promise<void>;
  maxDisplayItems?: number;
}

/**
 * Unified tags field cell component for both table and modal contexts
 * Handles display with badges and inline editing via modal
 * Eliminates duplication between table and detail views
 */
export function TagsFieldCell({
  value,
  fieldId,
  entityId,
  entityName,
  onSave,
  maxDisplayItems = 10,
}: TagsFieldCellProps) {
  const globalModalId = `list-editor-${entityName}-${entityId}-${fieldId}`;

  const handleSave = useCallback(
    async (newValue: string[]) => {
      try {
        await onSave(newValue);
      } catch (error) {
        console.error("Error saving tags field:", error);
        throw error;
      }
    },
    [onSave]
  );

  return (
    <div className="min-w-0 flex items-center gap-2">
      <TooltipWithContent content="Click to view and edit tags">
        <div className="flex-1 cursor-pointer hover:opacity-80 transition-opacity">
          <TagsFieldDisplay value={value} fieldId={fieldId} maxItems={maxDisplayItems} />
        </div>
      </TooltipWithContent>
      <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <ListFieldEditor
          value={value}
          fieldId={fieldId}
          onSave={handleSave}
          globalModalId={globalModalId}
          maxItems={20}
          entityName={entityName}
          entityId={entityId}
        />
      </div>
    </div>
  );
}
